import defaults from '../core/feel.json';
import { createGait } from './gait.js';
import { dressFigure } from './figure.js';

/**
 * A body you can look at, over a body the game collides.
 *
 * Three things that do not know about each other: `gait.js` says which state a
 * body is in, `clips.js` says which clips that state plays, and `figure.js`
 * plays them on the character. This is the only place they meet.
 *
 * ---------------------------------------------------------------------------
 * IT READS. IT DOES NOT WRITE.
 *
 * `pose()` takes a plain object of numbers and booleans. It holds no reference
 * to the player, to a cart, to the climbing module or to anything else that
 * could be changed by touching it, and there is no path from here back into the
 * simulation, which is the only way to promise that a rendering change cannot
 * move a measured number. The capsule is still the collision shape, at the same
 * radius and the same height, and the corridor benchmark neither knows nor
 * cares that anything was drawn.
 * ---------------------------------------------------------------------------
 */

/**
 * Hip to ankle, as a fraction of standing height. What a stride and a footstep
 * are measured against; the character's own legs are this long to within a
 * centimetre.
 */
const LEG = 0.47;

/** Radians in one frame past which a change of facing is a snap, not a turn. */
const SNAP = 0.6;

export function createRig({
  height = defaults.height,
  radius = defaults.radius,
  colour = 0x3c4452,
  accent = 0xe8e4dc,
  /** Whose skin and hair: anything stable per person. See `figure.js`. */
  look = null,
  /** A whole look (`appearance.js`): build, hair, face and clothes. */
  style = null,
} = {}) {
  const figure = dressFigure({ height, colour, accent, look, style });
  const gait = createGait();
  const group = figure.object3D;
  const legLength = LEG * height;
  let last = gait.read();
  /*
   * How fast the drawn body is turning, from the facing it is placed at frame
   * to frame: for stepping in place and banking (`figure.js`). A facing that
   * jumps further than a person turns in a frame is a teleport or a snap, and
   * is not a turn.
   */
  let facing = null;
  let lastFacing = null;
  let turn = 0;

  return {
    object3D: group,
    /** What the collision shape is, restated so a caller can check it has not moved. */
    capsule: { height, radius },
    legLength,

    /** Places the body at a position and a facing. Nothing else moves it. */
    sync(position, yaw) {
      group.position.set(position.x, position.y, position.z);
      group.rotation.y = yaw;
      facing = yaw;
    },

    /** One frame of animation, from a reading of the body. */
    pose(body, dt) {
      let raw = 0;
      if (facing !== null && lastFacing !== null && dt > 0) {
        const delta = Math.atan2(Math.sin(facing - lastFacing), Math.cos(facing - lastFacing));
        raw = Math.abs(delta) > SNAP ? 0 : delta / dt;
      }
      lastFacing = facing;
      turn += (raw - turn) * Math.min(1, dt * 12);
      const reading = { ...body, legLength, turn };
      last = gait.update(dt, reading);
      figure.animate(last, reading, dt);
      return last;
    },

    /** What it is currently playing: the state, and the clips. */
    playing: () => ({ ...gait.read(), clips: figure.read() }),

    setColor: figure.setColor,
    setAccent: figure.setAccent,
    /** The body itself, for a check: whether it is on, its parts and its bones. */
    figure,
    /** Back on its feet. Used when a run resets. */
    reset: () => {
      turn = 0;
      lastFacing = null;
      gait.reset();
      figure.reset();
    },

    dispose() {
      figure.dispose();
    },
  };
}
